import english from './locales/en-US/mobile-shop.json'
import german from './locales/de-DE/mobile-shop.json'
import { loadCrowdinMessages, validateCrowdinMessages } from './crowdin'
import { couponRewardLanguage } from '../utils/couponRewards'
import type { WhowsetLanguage } from '../types/whowset'
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { makeTranslator, normalizeLocale, parseBundle, type LocalizationBundle, type Translate } from './core'
const localeFiles = import.meta.glob(['./locales/*/mobile-shop.json', '!./locales/en-US/mobile-shop.json', '!./locales/de-DE/mobile-shop.json'], { import: 'default' })
interface ShopI18nValue {
    locale: string; language: WhowsetLanguage; t: Translate
    setLocale: (locale: string) => void
    applyLocalization: (bundle: LocalizationBundle) => void
}
// Isolated legacy components retain their original language; the application always mounts a provider.
const ShopI18nContext = createContext<ShopI18nValue>({ locale: 'de-DE', language: 'de', t: makeTranslator('de-DE'), setLocale: () => undefined, applyLocalization: () => undefined })
function browserLocale() {
    const params = new URLSearchParams(window.location.search)
    return normalizeLocale(params.get('locale') || params.get('lang') || navigator.language)
}
export function ShopI18nProvider({ children, initialLocale, bundle: suppliedBundle, loadRemote = true, manifestUrl = import.meta.env.VITE_CROWDIN_MANIFEST_URL }: { children: ReactNode; initialLocale?: string; bundle?: LocalizationBundle; loadRemote?: boolean; manifestUrl?: string }) {
    const [requestedLocale, setRequestedLocale] = useState(() => normalizeLocale(suppliedBundle?.locale || initialLocale || browserLocale()))
    const [bundle, setBundle] = useState<LocalizationBundle | undefined>(suppliedBundle)
    const [crowdin, setCrowdin] = useState<LocalizationBundle>()
    const applyLocalization = (next: LocalizationBundle) => {
        const validated = parseBundle(next)
        if (validated) { setRequestedLocale(validated.locale); setBundle(validated) }
    }
    useEffect(() => {
        if (!loadRemote || manifestUrl) return
        const controller = new AbortController()
        const base = import.meta.env.VITE_API_BASE_URL || window.location.origin
        const url = new URL('/api/mobile-shop/localization', base)
        url.searchParams.set('locale', requestedLocale)
        void fetch(url, { credentials: 'include', signal: controller.signal }).then(async response => {
            if (!response.ok) return
            const json = await response.json()
            if (json.r === false) return
            const next = parseBundle(json.data || json)
            if (next && !controller.signal.aborted) { setBundle(next); setRequestedLocale(next.locale) }
        }).catch(() => { /* English/source fallback keeps entry and checkout usable offline. */ })
        return () => controller.abort()
    }, [requestedLocale, loadRemote, manifestUrl])
    useEffect(() => {
        if (!manifestUrl) return
        const controller = new AbortController()
        const exact = `./locales/${requestedLocale}/mobile-shop.json`
        const language = requestedLocale.split('-')[0]
        const path = localeFiles[exact] ? exact : Object.keys(localeFiles).find(path => path.startsWith(`./locales/${language}-`))
        const fallback = language === 'en' ? Promise.resolve(english) : language === 'de' ? Promise.resolve(german) : path ? localeFiles[path]().then(validateCrowdinMessages).catch(() => ({})) : Promise.resolve({})
        void fallback.then(messages => { if (!controller.signal.aborted) setCrowdin({ locale: requestedLocale, messages }) })
        if (loadRemote) void Promise.all([fallback, loadCrowdinMessages(manifestUrl, requestedLocale, controller.signal)]).then(([local, remote]) => {
            if (!controller.signal.aborted) setCrowdin({ locale: requestedLocale, messages: { ...local, ...remote } })
        })
        return () => controller.abort()
    }, [requestedLocale, loadRemote, manifestUrl])
    const locale = requestedLocale
    useEffect(() => {
        document.documentElement.lang = locale
        document.documentElement.dir = /^(ar|fa|he|ur)(-|$)/i.test(locale) ? 'rtl' : 'ltr'
    }, [locale])
    const messages = manifestUrl ? (crowdin?.locale === locale ? crowdin.messages : undefined) : (bundle && normalizeLocale(bundle.locale) === locale ? bundle.messages : undefined)
    const t = useMemo(() => makeTranslator(locale, messages), [locale, messages])
    return <ShopI18nContext.Provider value={{ locale, language: couponRewardLanguage(locale), t, setLocale: value => setRequestedLocale(normalizeLocale(value)), applyLocalization }}>{children}</ShopI18nContext.Provider>
}
// Provider and hook intentionally form one small context module.
// eslint-disable-next-line react-refresh/only-export-components
export function useShopI18n() { return useContext(ShopI18nContext) }
